import { useState } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import type { MovimentacaoItemExecucao, MovimentacaoEquipamentoView, TipoFluxoMovimentacao } from '../../../models';
import {
  CONDICAO_EQUIPAMENTO_ENVIO_OPCOES,
  CONDICAO_EQUIPAMENTO_DEVOLUCAO_OPCOES,
  CONDICAO_EQUIPAMENTO_LABELS,
  ACESSORIO_OPCOES,
  ACESSORIO_LABELS,
} from '../solicitacaoDetailsPage.definitions';
import { useMovimentacaoMutations } from '../../../hooks/data';

interface CondicaoEquipamentoSectionProps {
  solicitacaoId: string;
  movimentacaoId: string;
  equipamentosExecucao: MovimentacaoItemExecucao[];
  item: MovimentacaoEquipamentoView;
  podeEditar: boolean;
  /** Define quais opções de Condição aparecem (envio: Novo/Usado; devolução: Ótima/Boa/
   * Péssima/Com defeito) — ver CONDICAO_EQUIPAMENTO_*_OPCOES. */
  tipoFluxo: TipoFluxoMovimentacao;
}

/** Condição, acessórios e observação do equipamento — os três vão no Termo. A observação é
 * sobre o estado do equipamento (arranhão, como foi entregue etc.), diferente da observação
 * geral da Solicitação. */
export default function CondicaoEquipamentoSection({
  solicitacaoId,
  movimentacaoId,
  equipamentosExecucao,
  item,
  podeEditar,
  tipoFluxo,
}: CondicaoEquipamentoSectionProps) {
  const { atualizarMovimentacao } = useMovimentacaoMutations(solicitacaoId);

  const [editando, setEditando] = useState(false);
  const [condicaoInput, setCondicaoInput] = useState('');
  const [acessoriosInput, setAcessoriosInput] = useState<string[]>([]);
  const [acessoriosOutrosInput, setAcessoriosOutrosInput] = useState('');
  const [temObservacao, setTemObservacao] = useState(false);
  const [observacaoInput, setObservacaoInput] = useState('');

  const condicaoOpcoes =
    tipoFluxo === 'envio' ? CONDICAO_EQUIPAMENTO_ENVIO_OPCOES : CONDICAO_EQUIPAMENTO_DEVOLUCAO_OPCOES;

  const handleAbrirEdicao = () => {
    setCondicaoInput(item.condicao ?? '');
    setAcessoriosInput(item.acessorios ?? []);
    setAcessoriosOutrosInput(item.acessoriosOutros ?? '');
    setTemObservacao(Boolean(item.observacao));
    setObservacaoInput(item.observacao ?? '');
    setEditando(true);
  };

  const toggleAcessorio = (valor: string) => {
    setAcessoriosInput((atual) => (atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor]));
  };

  const handleSalvar = () => {
    const dadosItem = {
      condicao: condicaoInput || null,
      acessorios: acessoriosInput.length > 0 ? acessoriosInput : null,
      acessoriosOutros: acessoriosOutrosInput.trim() || null,
      observacao: temObservacao ? observacaoInput.trim() || null : null,
    };
    const jaTinhaEntrada = equipamentosExecucao.some((equipamento) => equipamento.id === item.id);
    const novosExecucao = jaTinhaEntrada
      ? equipamentosExecucao.map((equipamento) =>
          equipamento.id === item.id ? { ...equipamento, ...dadosItem } : equipamento
        )
      : [...equipamentosExecucao, { id: item.id, ...dadosItem }];
    atualizarMovimentacao.mutate({ id: movimentacaoId, equipamentosExecucao: novosExecucao });
    setEditando(false);
  };

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Condição, acessórios e Observações</p>
      {!editando ? (
        <div className="mt-1 flex items-start justify-between gap-2">
          <div className="text-sm text-gray-700">
            <p>
              {item.condicao ? CONDICAO_EQUIPAMENTO_LABELS[item.condicao] ?? item.condicao : 'Condição não informada'}
            </p>
            {(item.acessorios?.length || item.acessoriosOutros) && (
              <p className="mt-0.5 text-gray-500">
                {[
                  ...(item.acessorios ?? []).map((valor) => ACESSORIO_LABELS[valor as keyof typeof ACESSORIO_LABELS] ?? valor),
                  item.acessoriosOutros,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            )}
            {item.observacao && <p className="mt-0.5 whitespace-pre-line text-gray-500">Obs.: {item.observacao}</p>}
          </div>
          {podeEditar && (
            <button
              type="button"
              className="shrink-0 text-gray-400 hover:text-gray-600"
              onClick={handleAbrirEdicao}
              title="Editar condição/acessórios/observação"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          <div>
            <p className="text-sm font-medium text-gray-700">Condição</p>
            <div className="mt-1 flex flex-wrap gap-3">
              {condicaoOpcoes.map((valor) => (
                <label key={valor} className="flex items-center gap-1.5 text-sm text-gray-700">
                  <input
                    type="radio"
                    name={`condicao-${item.id}`}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={condicaoInput === valor}
                    onChange={() => setCondicaoInput(valor)}
                  />
                  {CONDICAO_EQUIPAMENTO_LABELS[valor]}
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Acessórios</p>
            <div className="mt-1 flex flex-wrap gap-3">
              {/* Opção que saiu da lista (ex.: cartão de acesso) mas está marcada num registro
                  antigo continua aparecendo aqui, pra dar pra desmarcar. */}
              {[...ACESSORIO_OPCOES, ...acessoriosInput.filter((v) => !(ACESSORIO_OPCOES as readonly string[]).includes(v))].map((valor) => (
                <label key={valor} className="flex items-center gap-1.5 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={acessoriosInput.includes(valor)}
                    onChange={() => toggleAcessorio(valor)}
                  />
                  {ACESSORIO_LABELS[valor as keyof typeof ACESSORIO_LABELS] ?? valor}
                </label>
              ))}
            </div>
          </div>

          <input
            type="text"
            className="input-gaveta w-full"
            placeholder="Outros acessórios"
            value={acessoriosOutrosInput}
            onChange={(event) => setAcessoriosOutrosInput(event.target.value)}
          />

          <div>
            <p className="text-sm font-medium text-gray-700">Há alguma observação sobre o equipamento?</p>
            <div className="mt-1 flex gap-3">
              {[true, false].map((valor) => (
                <label key={String(valor)} className="flex items-center gap-1.5 text-sm text-gray-700">
                  <input
                    type="radio"
                    name={`tem-observacao-${item.id}`}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={temObservacao === valor}
                    onChange={() => setTemObservacao(valor)}
                  />
                  {valor ? 'Sim' : 'Não'}
                </label>
              ))}
            </div>
            {temObservacao && (
              <textarea
                className="input-gaveta mt-2 w-full"
                rows={2}
                placeholder="Estado do equipamento: arranhões, marcas de uso, como foi entregue..."
                value={observacaoInput}
                onChange={(event) => setObservacaoInput(event.target.value)}
              />
            )}
          </div>

          <div className="flex gap-2">
            <button type="button" className="btn-secondary text-sm" onClick={handleSalvar}>
              Salvar
            </button>
            <button type="button" className="text-sm text-gray-500 underline" onClick={() => setEditando(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
